import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TDTGraph from './TDTGraph';
import SISIGraph from './SISIGraph';
import ABLBGraph from './ABLBGraph';
import {
  TDT_FREQS, SISI_FREQS, ABLB_INTENSITIES,
  normaliseSpecial, interpretTDT, interpretSISI, interpretABLB, overallConclusion,
} from './specialLogic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand palette (Audinexa teal-blue + green negatives) — pixel-close
// to the reference. Kept inline so print CSS honours them without a
// tailwind-in-print gotcha.
const BRAND = {
  primary:      '#1a56db',    // header banner
  primarySoft:  '#e6efff',    // banner bg
  tableHead:    '#dbeafe',    // light teal
  tableAlt:     '#f8fafc',    // very light grey
  green:        '#059669',
  greenBg:      '#ecfdf5',
  amber:        '#d97706',
  amberBg:      '#fef3c7',
  red:          '#dc2626',
  redBg:        '#fee2e2',
  border:       '#cbd5e1',
  textMuted:    '#64748b',
};

const badgeSwatch = (tone) => tone === 'neg' ? { bg: BRAND.greenBg, color: BRAND.green, border: '#a7f3d0' }
  : tone === 'pos' ? { bg: BRAND.redBg, color: BRAND.red, border: '#fecaca' }
  : tone === 'mid' ? { bg: BRAND.amberBg, color: BRAND.amber, border: '#fde68a' }
  : { bg: '#f1f5f9', color: BRAND.textMuted, border: BRAND.border };

const TickIcon = ({ tone, size = 14 }) => {
  const c = tone === 'neg' ? BRAND.green : tone === 'pos' ? BRAND.red : tone === 'mid' ? BRAND.amber : BRAND.textMuted;
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" style={{ verticalAlign: 'middle' }}>
      <circle cx="10" cy="10" r="9" fill={c} />
      {tone === 'neg' && <path d="M6 10 l3 3 l5-6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
      {tone === 'pos' && <path d="M7 7 L13 13 M13 7 L7 13" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />}
      {tone === 'mid' && <path d="M10 6 v5 M10 13.5 v0.5" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />}
      {tone === 'none' && <text x="10" y="14" fill="#ffffff" fontSize="11" textAnchor="middle" fontWeight="700">?</text>}
    </svg>
  );
};

/**
 * SpecialReportView — read-only A4 report canvas.
 *
 * Layout mirrors the reference asset:
 *   • Row 1 (letterhead): clinic name/logo left · marketing tagline centre · "Better Care" script right
 *   • Row 2 (title band): AUDIOMETRIC SPECIAL TESTS REPORT · TDT | SISI | ABLB
 *   • Row 3 (info): 3 sub-columns of patient / clinical / equipment fields
 *   • Row 4 (body): 3 columns — TDT | SISI | ABLB
 *   • Row 5: Overall Conclusion (left) + Recommendations bullet list (right)
 *   • Footer: clinic contact · tagline · audiologist signature
 */
const SpecialReportView = ({ state, patient, clinic, audiologistName, license, sessionId }) => {
  const s = state;
  const tRes = interpretTDT(s);
  const sRes = interpretSISI(s);
  const aRes = interpretABLB(s);
  const overall = overallConclusion(tRes, sRes, aRes);
  const printedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const InfoCell = ({ label, value }) => (
    <div className="flex text-[9.5px] leading-tight">
      <div className="w-1/2 font-semibold" style={{ color: BRAND.textMuted }}>{label}</div>
      <div className="w-1/2" style={{ color: '#0f172a' }}>: {value || '—'}</div>
    </div>
  );

  const SectionCol = ({ title, subtitle, procedure, graph, table, result, testId }) => {
    const swatch = badgeSwatch(result.tone);
    return (
      <div className="flex flex-col border-r px-2" style={{ borderColor: BRAND.border }} data-testid={testId}>
        <h2 className="text-[11px] font-black uppercase mb-0.5" style={{ color: BRAND.primary, letterSpacing: 0.3 }}>{title}</h2>
        <div className="text-[9px] italic mb-1" style={{ color: BRAND.textMuted }}>{subtitle}</div>
        <div className="text-[9px] mb-1" style={{ color: '#334155' }}>
          <span className="font-bold" style={{ color: '#0f172a' }}>Procedure: </span>
          <span className="italic">{procedure}</span>
        </div>
        {graph}
        {table}
        <div className="mt-1">
          <div className="text-[10px] font-bold uppercase" style={{ color: '#0f172a' }}>Interpretation</div>
          <div
            className="mt-0.5 rounded px-1.5 py-1 flex items-start gap-1.5 border"
            style={{ backgroundColor: swatch.bg, borderColor: swatch.border }}
          >
            <TickIcon tone={result.tone} size={16} />
            <div>
              <div className="text-[10px] font-bold" style={{ color: swatch.color }}>{result.label}</div>
              <div className="text-[9px]" style={{ color: '#334155' }}>{result.note}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const dataTable = (rows, headers, testId) => (
    <table className="w-full text-[9px] border mt-1" style={{ borderColor: BRAND.border, borderCollapse: 'collapse' }} data-testid={testId}>
      <thead>
        <tr style={{ backgroundColor: BRAND.tableHead }}>
          {headers.map((h) => <th key={h} className="border px-1 py-0.5 font-bold" style={{ borderColor: BRAND.border, color: '#0f172a' }}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
            {r.map((c, j) => <td key={j} className="border px-1 py-0.5 text-center" style={{ borderColor: BRAND.border, color: '#0f172a' }}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const tdtRows = TDT_FREQS.map((f) => {
    const raw = s.tdt.values?.[String(f)];
    const v = (raw === undefined || raw === null || raw === '') ? '—' : (Number(raw) >= 60 ? '> 60' : Number(raw));
    return [f.toLocaleString(), v];
  });
  const sisiRows = SISI_FREQS.map((f) => {
    const r = s.sisi.right?.[String(f)];
    const l = s.sisi.left?.[String(f)];
    const anyLeft = Object.keys(s.sisi.left || {}).length > 0;
    if (anyLeft) return [f.toLocaleString(), r ?? '—', l ?? '—'];
    return [f.toLocaleString(), r ?? '—'];
  });
  const sisiHeaders = Object.keys(s.sisi.left || {}).length > 0
    ? ['Frequency (Hz)', 'RE Score (%)', 'LE Score (%)']
    : ['Frequency (Hz)', 'SISI Score (%)'];
  const ablbRows = ABLB_INTENSITIES.map((i) => {
    const rv = s.ablb.right?.[String(i)];
    const anyLeft = Object.keys(s.ablb.left || {}).length > 0;
    if (anyLeft) {
      const lv = s.ablb.left?.[String(i)];
      const diff = rv !== undefined && rv !== null && rv !== '' && Number.isFinite(Number(rv)) ? Math.round(Number(rv) - i) : (lv !== undefined && lv !== null && lv !== '' && Number.isFinite(Number(lv)) ? Math.round(Number(lv) - i) : '—');
      return [i, rv ?? '—', lv ?? '—', diff];
    }
    const diff = rv !== undefined && rv !== null && rv !== '' && Number.isFinite(Number(rv)) ? Math.round(Number(rv) - i) : '—';
    return [i, rv ?? '—', diff];
  });
  const ablbHeaders = Object.keys(s.ablb.left || {}).length > 0
    ? ['Test Ear (dB HL)', 'RE Match', 'LE Match', 'Δ (dB)']
    : ['Test Ear Intensity (dB HL)', 'Non-Test Ear Match (dB HL)', 'Interaural Difference (dB)'];

  return (
    <div
      id="special-report-canvas"
      data-testid="special-report-canvas"
      className="special-report bg-white text-slate-900"
      style={{
        width: '210mm', minHeight: '297mm',
        padding: '8mm', boxSizing: 'border-box',
        fontFamily: 'Arial, Helvetica, sans-serif', color: '#0f172a',
        WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
      }}
    >
      {/* ── Letterhead ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b pb-1 mb-1.5" style={{ borderColor: BRAND.border }}>
        <div className="flex items-center gap-2">
          {clinic?.logo_base64 && <img src={clinic.logo_base64} alt="logo" style={{ width: 52, height: 52, objectFit: 'contain' }} />}
          <div>
            <div className="text-[16px] font-black" style={{ color: BRAND.primary, letterSpacing: 0.5 }}>{clinic?.name || 'audinexa'}</div>
            {clinic?.tagline && <div className="text-[9px] italic" style={{ color: BRAND.textMuted }}>{clinic.tagline}</div>}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-bold" style={{ color: '#0f172a' }}>Smarter Audiology.</div>
          <div className="text-[10px] font-bold" style={{ color: '#0f172a' }}>Healthier Hearing Futures.</div>
        </div>
        <div className="text-right" style={{ color: BRAND.primary, fontFamily: 'cursive' }}>
          <div className="text-[13px] italic font-semibold">Better Care</div>
          <div className="text-[13px] italic font-semibold">Brighter Lives</div>
        </div>
      </div>

      {/* ── Title band ─────────────────────────────────────────── */}
      <div
        className="text-center py-1.5 rounded"
        style={{ backgroundColor: BRAND.primarySoft }}
        data-testid="special-report-title-band"
      >
        <div className="text-[15px] font-black uppercase" style={{ color: BRAND.primary, letterSpacing: 1 }}>
          Audiometric Special Tests Report
        </div>
        <div className="text-[10px] font-bold tracking-widest" style={{ color: BRAND.primary }}>TDT&nbsp;&nbsp;|&nbsp;&nbsp;SISI&nbsp;&nbsp;|&nbsp;&nbsp;ABLB</div>
      </div>

      {/* ── Info grid (3 sub-columns) ──────────────────────────── */}
      <div className="grid grid-cols-3 gap-2 mt-2 border rounded px-2 py-1.5" style={{ borderColor: BRAND.border }} data-testid="special-report-info">
        <div className="space-y-0.5">
          <InfoCell label="Patient Name" value={patient?.name} />
          <InfoCell label="Patient ID" value={patient?.mrd || patient?.patient_id} />
          <InfoCell label="Age / Gender" value={patient?.age ? `${patient.age} years / ${patient.gender ? patient.gender[0]?.toUpperCase() + patient.gender.slice(1) : ''}` : ''} />
          <InfoCell label="Date of Test" value={printedOn} />
          <InfoCell label="Refer by" value={s.refer_by} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Clinic Name" value={clinic?.name} />
          <InfoCell label="Clinician" value={s.clinician || audiologistName} />
          <InfoCell label="Test Environment" value={s.environment} />
          <InfoCell label="Transducer" value={s.transducer} />
          <InfoCell label="Test Ear" value={s.test_ear} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Audiometer" value={s.audiometer} />
          <InfoCell label="Calibration" value={s.calibration} />
          <InfoCell label="Test Standards" value={s.test_standards} />
          <InfoCell label="Purpose" value={s.purpose} />
          <InfoCell label="Diagnosis Context" value={s.diagnosis_context} />
        </div>
      </div>

      {/* ── Three-column body ──────────────────────────────────── */}
      <div className="grid grid-cols-3 mt-2" data-testid="special-report-body">
        <SectionCol
          testId="special-report-tdt"
          title="Tone Decay Test (TDT)"
          subtitle="Assessment of auditory fatigue"
          procedure={s.tdt.procedure}
          graph={<div className="my-1"><TDTGraph values={s.tdt.values} readOnly width={330} height={140} /></div>}
          table={dataTable(tdtRows, ['Frequency (Hz)', 'Tone Decay Time (seconds)'], 'special-report-tdt-table')}
          result={tRes}
        />
        <SectionCol
          testId="special-report-sisi"
          title="Short Increment Sensitivity Index (SISI)"
          subtitle="Assessment of small intensity change detection"
          procedure={s.sisi.procedure}
          graph={<div className="my-1"><SISIGraph right={s.sisi.right} left={s.sisi.left} readOnly width={330} height={140} /></div>}
          table={dataTable(sisiRows, sisiHeaders, 'special-report-sisi-table')}
          result={sRes}
        />
        <div className="flex flex-col px-2" data-testid="special-report-ablb">
          <h2 className="text-[11px] font-black uppercase mb-0.5" style={{ color: BRAND.primary, letterSpacing: 0.3 }}>Alternate Binaural Loudness Balance (ABLB)</h2>
          <div className="text-[9px] italic mb-1" style={{ color: BRAND.textMuted }}>Assessment of recruitment</div>
          <div className="text-[9px] mb-1" style={{ color: '#334155' }}>
            <span className="font-bold" style={{ color: '#0f172a' }}>Procedure: </span>
            <span className="italic">{s.ablb.procedure}</span>
          </div>
          <div className="my-1"><ABLBGraph right={s.ablb.right} left={s.ablb.left} readOnly width={330} height={140} /></div>
          {dataTable(ablbRows, ablbHeaders, 'special-report-ablb-table')}
          <div className="mt-1">
            <div className="text-[10px] font-bold uppercase" style={{ color: '#0f172a' }}>Interpretation</div>
            <div
              className="mt-0.5 rounded px-1.5 py-1 flex items-start gap-1.5 border"
              style={{ backgroundColor: badgeSwatch(aRes.tone).bg, borderColor: badgeSwatch(aRes.tone).border }}
            >
              <TickIcon tone={aRes.tone} size={16} />
              <div>
                <div className="text-[10px] font-bold" style={{ color: badgeSwatch(aRes.tone).color }}>{aRes.label}</div>
                <div className="text-[9px]" style={{ color: '#334155' }}>{aRes.note}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Overall + Recommendations ──────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 mt-3" data-testid="special-report-overall-row">
        <div>
          <div className="text-[11px] font-bold uppercase" style={{ color: '#0f172a' }}>Overall Conclusion</div>
          <div
            className="mt-1 rounded px-2 py-1.5 flex items-start gap-2 border"
            style={{ backgroundColor: badgeSwatch(overall.tone).bg, borderColor: badgeSwatch(overall.tone).border }}
            data-testid="special-report-overall"
          >
            <TickIcon tone={overall.tone} size={20} />
            <div>
              <div className="text-[11px] font-bold" style={{ color: badgeSwatch(overall.tone).color }}>{overall.label}</div>
              <div className="text-[9.5px] whitespace-pre-wrap" style={{ color: '#334155' }}>{s.overall_conclusion || overall.note}</div>
            </div>
          </div>
        </div>
        <div data-testid="special-report-recommendations">
          <div className="text-[11px] font-bold uppercase" style={{ color: '#0f172a' }}>Recommendations</div>
          <div className="mt-1 text-[10px]" style={{ color: '#0f172a' }}>
            {s.recommendations
              ? s.recommendations.split(/\n+/).filter(Boolean).map((line, i) => (
                  <div key={i} className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>{line.replace(/^[•\-*]\s*/, '')}</span></div>
                ))
              : (
                <>
                  <div className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>Continue routine audiological monitoring as advised.</span></div>
                  <div className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>If symptoms persist, consider additional tests (speech in noise, imaging) based on clinical suspicion.</span></div>
                  <div className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>Correlate with full audiological evaluation.</span></div>
                </>
              )}
          </div>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="flex items-end justify-between mt-3 pt-2 border-t" style={{ borderColor: BRAND.border }} data-testid="special-report-footer">
        <div>
          <div className="text-[11px] font-bold" style={{ color: BRAND.primary }}>{clinic?.name || 'Audinexa Hearing Clinic'}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>📍 {clinic?.address_line1}{clinic?.address_line2 ? `, ${clinic.address_line2}` : ''}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>
            {clinic?.tel && <>📞 {clinic.tel}&nbsp;&nbsp;</>}
            {clinic?.email && <>✉ {clinic.email}</>}
          </div>
        </div>
        <div className="text-center text-[10px] italic" style={{ color: '#334155' }}>
          <div>Technology for Today.</div>
          <div>Better Hearing for Tomorrow.</div>
        </div>
        <div className="text-right">
          <div className="border-t pt-0.5 min-w-[140px]" style={{ borderColor: '#475569' }}>
            <div className="font-bold text-[11px]">{audiologistName || 'Audiologist'}</div>
            <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Audiologist</div>
            {license && <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Reg. No.: {license}</div>}
          </div>
        </div>
      </div>
      {sessionId && <div className="mt-1 text-[8px] text-right" style={{ color: BRAND.textMuted }}>Ref: {sessionId}</div>}
    </div>
  );
};

/**
 * Wrapper that fetches clinic branding + license from settings once
 * per open. Used by SpecialReportModal.
 */
export const SpecialReportViewWithFetch = ({ state, patient, sessionId, audiologistName }) => {
  const [clinic, setClinic] = useState({ name: '', tagline: '', address_line1: '', address_line2: '', tel: '', email: '', logo_base64: null });
  const [license, setLicense] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/clinic${sessionId ? `?session_id=${sessionId}` : ''}`);
        const c = r.data || {};
        if (!alive) return;
        setClinic({
          name: c.name || '',
          tagline: c.tagline || '',
          address_line1: c.address || '',
          address_line2: [c.city, c.state, c.pincode].filter(Boolean).join(', '),
          tel: c.phone || '',
          email: c.email || '',
          logo_base64: null,
        });
        try {
          const lr = await axios.get(`${API}/settings/clinic/logo`, { responseType: 'blob' });
          if (!alive) return;
          setClinic((prev) => ({ ...prev, logo_base64: URL.createObjectURL(lr.data) }));
        } catch { /* no logo */ }
      } catch { /* fallback */ }
      try {
        if (sessionId) {
          const sr = await axios.get(`${API}/sessions/${sessionId}`);
          const uid = sr.data?.audiologist_user_id;
          if (uid && alive) {
            const lr = await axios.get(`${API}/settings/users/${uid}/signature`, { responseType: 'blob' });
            const lic = lr?.headers?.['x-license-no'] || lr?.headers?.['X-License-No'] || '';
            if (alive) setLicense(lic);
          }
        }
      } catch { /* no license */ }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <SpecialReportView
      state={state}
      patient={patient}
      clinic={clinic}
      audiologistName={audiologistName}
      license={license}
      sessionId={sessionId}
    />
  );
};

export default SpecialReportView;
